import {Map as IMap, Iterable as IIterable} from 'immutable';
import difference from 'lodash/difference';
import intersection from 'lodash/intersection';

// Example:
//   IMap({
//     '64a2f23b2c8d0a4e150a410e': IMap({
//       'slug': 'some-translated-slug',
//       'seoTitle': 'Some localized SEO title.',
//     })
//   })
export type ImmutableLocalizedSettings = IMap<
  // locale id
  string,
  IMap<string, IMap<string, any>>
>;

// Example:
//   {
//     '64a2f23b2c8d0a4e150a410e': {
//       'slug': 'some-translated-slug',
//       'seoTitle': 'Some localized SEO title.',
//     }
//   }
export type PlainLocalizedSettings = {
  [localeId: string]: {
    [key: string]: any;
  };
};

// Utils to help operate on both `Collection.localizedSettings` and `Page.localizedSettings`.
export class LocalizedSettingsUtils {
  static extractLocaleIdsFrom(
    leftLocalizedSettings?:
      | ImmutableLocalizedSettings
      | PlainLocalizedSettings
      | null,
    rightLocalizedSettings?:
      | ImmutableLocalizedSettings
      | PlainLocalizedSettings
      | null,
    options?: {
      mapLeftIds?: (localeId: string) => string | null | undefined;
      mapRightIds?: (localeId: string) => string | null | undefined;
    }
  ): {
    idsInLeftOnly: string[];
    idsInBoth: string[];
    idsInRightOnly: string[];
  } {
    let idsInLeft: string[] = !leftLocalizedSettings
      ? []
      : IIterable.isIterable(leftLocalizedSettings)
        ? typedAsStrings(
            (leftLocalizedSettings as ImmutableLocalizedSettings)
              .keySeq()
              .toJS()
          )
        : Object.keys(leftLocalizedSettings);

    const mapLeftIds = options?.mapLeftIds;
    if (mapLeftIds) {
      // This can be read as a .map().filter(Boolean), but Typescript refinement isn't great with .filter(Boolean)
      idsInLeft = idsInLeft.reduce((acc, id) => {
        const mappedId = mapLeftIds(id);
        if (mappedId) {
          acc.push(mappedId);
        }
        return acc;
      }, [] as string[]);
    }

    let idsInRight: string[] = !rightLocalizedSettings
      ? []
      : IIterable.isIterable(rightLocalizedSettings)
        ? typedAsStrings(
            (rightLocalizedSettings as ImmutableLocalizedSettings)
              .keySeq()
              .toJS()
          )
        : Object.keys(rightLocalizedSettings);

    const mapRightIds = options?.mapRightIds;
    if (mapRightIds) {
      // This can be read as a .map().filter(Boolean), but Typescript refinement isn't great with .filter(Boolean)
      idsInRight = idsInRight.reduce((acc, id) => {
        const mappedId = mapRightIds(id);
        if (mappedId) {
          acc.push(mappedId);
        }
        return acc;
      }, [] as string[]);
    }

    return {
      idsInLeftOnly: difference(idsInLeft, idsInRight),
      idsInBoth: intersection(idsInLeft, idsInRight),
      idsInRightOnly: difference(idsInRight, idsInLeft),
    };
  }

  static getLocalizedField<
    T extends {
      [key: string]: any;
    },
    K extends keyof T,
  >(object: T, localeId: string | null | undefined, key: K): T[K] {
    if (!localeId || !object.localizedSettings) {
      return object[key];
    }

    return object.localizedSettings?.[localeId]?.[key] ?? object[key];
  }

  static isAnyLocalizedSlugModified(
    prevLocalizedSettings: PlainLocalizedSettings,
    nextLocalizedSettings: PlainLocalizedSettings
  ): boolean {
    const {idsInLeftOnly, idsInBoth, idsInRightOnly} =
      LocalizedSettingsUtils.extractLocaleIdsFrom(
        prevLocalizedSettings,
        nextLocalizedSettings
      );
    return (
      idsInLeftOnly.some((localeId) =>
        Boolean(prevLocalizedSettings?.[localeId]?.slug)
      ) ||
      idsInRightOnly.some((localeId) =>
        Boolean(nextLocalizedSettings?.[localeId]?.slug)
      ) ||
      idsInBoth.some(
        (localeId) =>
          prevLocalizedSettings &&
          nextLocalizedSettings &&
          prevLocalizedSettings[localeId]?.slug !==
            nextLocalizedSettings[localeId]?.slug
      )
    );
  }
}

function typedAsStrings(array: unknown[]): string[] {
  return array as string[];
}
